import React, { useState } from 'react';
import { CheckCircle2Icon, AlertCircleIcon, SendIcon, Loader2Icon } from 'lucide-react';
type Fields = {
  name: string;
  email: string;
  phone: string;
  message: string;
};
const empty: Fields = {
  name: '',
  email: '',
  phone: '',
  message: ''
};
export function ContactForm({
  compact = false
}: {
  compact?: boolean;
}) {
  const [values, setValues] = useState<Fields>(empty);
  const [errors, setErrors] = useState<Partial<Fields>>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const update = (field: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setValues(v => ({
      ...v,
      [field]: e.target.value
    }));
    setErrors(err => ({
      ...err,
      [field]: undefined
    }));
  };
  const validate = (): boolean => {
    const next: Partial<Fields> = {};
    if (!values.name.trim()) next.name = 'Please enter your name.';
    if (!values.email.trim()) {
      next.email = 'Please enter your e-mail.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      next.email = 'Please enter a valid e-mail address.';
    }
    if (!values.message.trim()) next.message = 'Please tell us what you need done.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('sending');
    window.setTimeout(() => {
      setStatus('sent');
      setValues(empty);
    }, 900);
  };
  if (status === 'sent') {
    return <div className="rounded-sm border border-pine/30 bg-white p-8 text-center shadow-card" data-id="element-64" data-mp="3bni18ow86hxh">
        <CheckCircle2Icon className="mx-auto h-12 w-12 text-pine" aria-hidden="true" data-id="element-65" data-mp="3gml4q4wk4g3c" />
        <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-ink" data-id="element-66" data-mp="3kjr1g5s11kko">
          Request received
        </h3>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-steel-500" data-id="element-67" data-mp="zv37sq2201nf">
          Thank you — your request for a free estimate has been submitted. If
          your situation is urgent, calling is the fastest way to reach us.
        </p>
        <button type="button" onClick={() => setStatus('idle')} className="mt-5 rounded-sm border-2 border-ink px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-ink transition hover:bg-ink hover:text-white" data-id="element-68" data-mp="2rzichmi8jo15">
          Send another request
        </button>
      </div>;
  }
  const field = `w-full rounded-sm border border-steel-300 bg-white px-4 text-base text-ink placeholder:text-steel-400 focus:border-safety focus:outline-none focus:ring-2 focus:ring-safety/30 ${compact ? 'h-12 py-0' : 'py-3.5'}`;
  return <form onSubmit={onSubmit} noValidate className={`h-full rounded-sm border border-steel-200 bg-white shadow-card ${compact ? 'p-5' : 'p-6 sm:p-8'}`} data-id="element-69" data-mp="2i61jgkpxacyj">
      <h3 className={`font-display font-bold uppercase tracking-tight text-ink ${compact ? 'text-xl' : 'text-2xl'}`} data-id="element-70" data-mp="1pi5q9pzkb36i">
        Schedule a FREE Estimate
      </h3>
      <p className="mt-1 text-sm text-steel-500" data-id="element-71" data-mp="9sel2vgo2aa5">
        {compact ? 'Free consultation — no cost, no obligation.' : 'Send us the details and we will follow up to set up your free consultation.'}
      </p>

      <div className={`grid ${compact ? 'mt-4 gap-3' : 'mt-6 gap-5 sm:grid-cols-2'}`} data-id="element-72" data-mp="x6vxj1vru4o">
        <div className={compact ? '' : 'sm:col-span-1'} data-id="element-73" data-mp="opnervtfzq51">
          <label htmlFor="thi-name" className="mb-1.5 block text-sm font-semibold uppercase tracking-wide text-ink" data-id="element-74" data-mp="sqz4e1cbgbd8">
            Your Name
          </label>
          <input id="thi-name" name="name" type="text" value={values.name} onChange={update('name')} aria-invalid={Boolean(errors.name)} className={field} placeholder="First and last name" data-id="element-75" data-mp="jp9gh5hn7uk7" />
          <FieldError message={errors.name} data-id="element-76" data-mp="3jq6m4uuovhwr" />
        </div>

        <div className={compact ? '' : 'sm:col-span-1'} data-id="element-77" data-mp="3jrrgokh070um">
          <label htmlFor="thi-email" className="mb-1.5 block text-sm font-semibold uppercase tracking-wide text-ink" data-id="element-78" data-mp="3exvmlt3v1y4q">
            E-mail
          </label>
          <input id="thi-email" name="email" type="email" value={values.email} onChange={update('email')} aria-invalid={Boolean(errors.email)} className={field} placeholder="you@example.com" data-id="element-79" data-mp="35z1hfecgrzfk" />
          <FieldError message={errors.email} data-id="element-80" data-mp="3tn1jvbj51e9z" />
        </div>

        <div className={compact ? '' : 'sm:col-span-2'} data-id="element-81" data-mp="3tpnpv6qdrlq3">
          <label htmlFor="thi-phone" className="mb-1.5 block text-sm font-semibold uppercase tracking-wide text-ink" data-id="element-82" data-mp="3onyc3jbrwtyb">
            Phone Number
          </label>
          <input id="thi-phone" name="phone" type="tel" value={values.phone} onChange={update('phone')} className={field} placeholder="(248) 000-0000" data-id="element-83" data-mp="2d8jkkv2c2ual" />
        </div>

        <div className={compact ? '' : 'sm:col-span-2'} data-id="element-84" data-mp="2d43p7qyglzck">
          <label htmlFor="thi-message" className="mb-1.5 block text-sm font-semibold uppercase tracking-wide text-ink" data-id="element-85" data-mp="2d2iuo1c5agep">
            Your Message
          </label>
          <textarea id="thi-message" name="message" rows={compact ? 3 : 5} value={values.message} onChange={update('message')} aria-invalid={Boolean(errors.message)} className="w-full rounded-sm border border-steel-300 bg-white px-4 py-3 text-base text-ink placeholder:text-steel-400 focus:border-safety focus:outline-none focus:ring-2 focus:ring-safety/30" placeholder={compact ? 'What needs repair, updating or renovating — and your city.' : 'Tell us about the project — what needs repair, updating or renovating, and the city you are in.'} data-id="element-86" data-mp="2lvm7q8ivor8t" />
          <FieldError message={errors.message} data-id="element-87" data-mp="thp9pmkzdrsb" />
        </div>
      </div>

      <button type="submit" disabled={status === 'sending'} className={`flex w-full items-center justify-center gap-2 rounded-sm bg-safety px-6 text-base font-bold uppercase tracking-wide text-white transition hover:bg-safety-600 disabled:opacity-70 ${compact ? 'mt-4 h-12' : 'mt-6 py-4'}`} data-id="element-88" data-mp="18dn00o8841wm">
        {status === 'sending' ? <>
            <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-89" data-mp="1r21rfcmgdhxy" />
            Sending…
          </> : <>
            <SendIcon className="h-5 w-5" aria-hidden="true" data-id="element-90" data-mp="1m6mgotjqs25r" />
            Request My Free Estimate
          </>}
      </button>
    </form>;
}
function FieldError({
  message
}: {
  message?: string;
}) {
  if (!message) return null;
  return <p className="mt-1.5 flex items-center gap-1.5 text-sm font-medium text-safety-600" data-id="element-91" data-mp="2ert875axmdzv">
      <AlertCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-92" data-mp="2jq363no1jsp6" />
      {message}
    </p>;
}